import React, { useState, useEffect, useMemo } from 'react';
import { useNetworkContext } from '@/contexts/NetworkContext';
import { Hub, FileType } from '@/types/data';
import { DataGrid, type Column, textEditor, type SortColumn } from 'react-data-grid';
import { useHubsData } from '@/hooks/useHubsData';
// Using direct path references to SVG files in the public directory
const trashIcon = '/trash.svg';
const saveIcon = '/save.svg';
const infoIcon = '/info.svg';
import { EmptyState } from '@/components/common/EmptyState';
import { HubsHeader } from '@/components/RightPanel/HubsHeader';
import { AutosaveIndicator } from '@/components/common/AutosaveIndicator';

const HubsTab: React.FC = () => {
  const { currentNetwork } = useNetworkContext();
  const { hubs, isEdited, isSaving, addEmptyRow, discardChanges, handleRowsChange, deleteRow } =
    useHubsData(currentNetwork);
  const [isLoading, setIsLoading] = useState(true);
  const [sortColumns, setSortColumns] = useState<readonly SortColumn[]>([]);

  // Set loading state when network changes
  useEffect(() => {
    setIsLoading(true);
    // Short timeout to simulate loading and prevent flickering
    const timer = setTimeout(() => {
      setIsLoading(false);
    }, 300);
    return () => clearTimeout(timer);
  }, [currentNetwork]);

  // Get comparator function for sorting
  const getComparator = (sortColumn: string): ((a: Hub, b: Hub) => number) => {
    // All hub properties are strings, so we can use localeCompare for all columns
    return (a, b) => {
      return a[sortColumn as keyof Hub].localeCompare(b[sortColumn as keyof Hub]);
    };
  };

  // Apply sorting to rows
  const sortedRows = useMemo((): readonly Hub[] => {
    if (sortColumns.length === 0) return hubs;

    return [...hubs].sort((a, b) => {
      for (const sort of sortColumns) {
        const comparator = getComparator(sort.columnKey);
        const compResult = comparator(a, b);
        if (compResult !== 0) {
          return sort.direction === 'ASC' ? compResult : -compResult;
        }
      }
      return 0;
    });
  }, [hubs, sortColumns]);

  // Define columns for the data grid
  const columns: Column<Hub>[] = [
    {
      key: 'hub',
      name: 'Hub ID',
      sortable: true,
      renderEditCell: textEditor,
      width: 80,
      resizable: true,
    },
    {
      key: 'name',
      name: 'Hub Name',
      sortable: true,
      renderEditCell: textEditor,
      width: 150,
      resizable: true,
    },
    {
      key: 'street',
      name: 'Street Address',
      sortable: true,
      renderEditCell: textEditor,
      width: 200,
      resizable: true,
    },
    {
      key: 'suburb',
      name: 'Suburb/City',
      sortable: true,
      renderEditCell: textEditor,
      width: 120,
      resizable: true,
    },
    {
      key: 'state',
      name: 'State/Province',
      sortable: true,
      renderEditCell: textEditor,
      width: 120,
      resizable: true,
    },
    {
      key: 'postcode',
      name: 'Postal Code',
      sortable: true,
      renderEditCell: textEditor,
      width: 100,
      resizable: true,
    },
    {
      key: 'country_code',
      name: 'Country',
      sortable: true,
      renderEditCell: textEditor,
      width: 80,
      resizable: true,
    },
    {
      key: 'company',
      name: 'Company',
      sortable: true,
      renderEditCell: textEditor,
      width: 150,
      resizable: true,
    },
    {
      key: 'delete',
      name: '',
      width: 50,
      renderCell: ({ rowIdx }) => (
        <button
          onClick={() => deleteRow(rowIdx)}
          className="text-red-600 hover:text-red-800 p-1 transition-colors"
          title="Delete this hub"
        >
          <img src={trashIcon} alt="Delete" className="h-5 w-5" />
        </button>
      ),
    },
  ];

  if (!currentNetwork) {
    return (
      <EmptyState
        message="No network selected"
        subMessage="Please select a network from the left panel to view and edit hubs."
      />
    );
  }

  const hubsFile = currentNetwork.files.find((file) => file.type === FileType.Hubs);

  if (!hubsFile) {
    return (
      <EmptyState
        message="No hubs file found"
        subMessage="The selected network does not contain a hubs file. Please select a different network or add a hubs file to this network."
      />
    );
  }

  if (isLoading) {
    return (
      <div className="py-4 flex justify-center items-center h-64">
        <div className="animate-pulse text-indigo-600">Loading hubs data...</div>
      </div>
    );
  }

  return (
    <div className="py-4">
      <HubsHeader addEmptyRow={addEmptyRow} discardChanges={discardChanges} isEdited={isEdited}>
        <AutosaveIndicator isSaving={isSaving} isEdited={isEdited} />
      </HubsHeader>

      <div className="border rounded overflow-hidden shadow-sm mb-4">
        <DataGrid
          columns={columns}
          rows={sortedRows}
          onRowsChange={handleRowsChange}
          className="rdg-light"
          style={{ height: 'calc(100vh - 250px)', minHeight: '400px' }}
          sortColumns={sortColumns}
          onSortColumnsChange={setSortColumns}
        />
      </div>

      <div
        className={`text-sm p-2 rounded ${isEdited ? 'bg-indigo-50 text-indigo-700' : 'text-gray-500'}`}
      >
        {isEdited ? (
          <div className="flex items-center">
            <img src={saveIcon} alt="Save" className="h-5 w-5" />
            <span className="ml-1">
              Your changes are automatically saved to local storage and will be applied when you
              export the network
            </span>
          </div>
        ) : (
          <div className="flex items-center">
            <img src={infoIcon} alt="Info" className="h-5 w-5" />
            <span className="ml-1">Changes will be automatically saved as you edit</span>
          </div>
        )}
      </div>
    </div>
  );
};

export default HubsTab;
